import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Package } from 'lucide-react';
import { OrderTimelineLineDto } from '../../../../../types/orderTimeline';

interface TimelineOrderLinesProps {
  lines: OrderTimelineLineDto[];
}

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

export const TimelineOrderLines: React.FC<TimelineOrderLinesProps> = ({ lines }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Package className="w-4 h-4" />
        Ordrelinjer ({lines.length})
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600">
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Linje</th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Varenr</th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Beskrivelse</th>
                <th className="text-right px-2 py-1.5 font-medium border-b border-gray-200">Bestilt</th>
                <th className="text-right px-2 py-1.5 font-medium border-b border-gray-200">Plukket</th>
                <th className="text-right px-2 py-1.5 font-medium border-b border-gray-200">Levert</th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Enhet</th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Status</th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Lokasjon</th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Plukket av</th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">PList</th>
                <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Opprettet</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const isMismatch = line.quantity !== line.quantityPicked;
                const isDeliveryMissing = line.quantityPicked > 0 && line.quantityDelivered === 0;
                return (
                  <tr key={line.id} className={`border-b border-gray-100 hover:bg-gray-50 ${isMismatch ? 'bg-yellow-50' : ''}`}>
                    <td className="px-2 py-1.5 text-gray-600">{line.lineNo}</td>
                    <td className="px-2 py-1.5 font-mono font-medium text-gray-800">{line.itemNo}</td>
                    <td className="px-2 py-1.5 text-gray-600 max-w-[200px] truncate">{line.description1 || '–'}</td>
                    <td className="px-2 py-1.5 text-right text-gray-800">{line.quantity}</td>
                    <td className={`px-2 py-1.5 text-right font-medium ${isMismatch ? 'text-yellow-700' : 'text-green-700'}`}>
                      {line.quantityPicked}
                    </td>
                    <td className={`px-2 py-1.5 text-right ${isDeliveryMissing ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                      {line.quantityDelivered}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500">{line.itemUnitCode || '–'}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        line.lineStatus >= 60 ? 'bg-green-100 text-green-700' :
                        line.lineStatus >= 40 ? 'bg-blue-100 text-blue-700' :
                        line.lineStatus >= 20 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {line.lineStatusText}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-600 font-mono text-[10px]">{line.stdLocationName || '–'}</td>
                    <td className="px-2 py-1.5 text-gray-600">{line.pickedBy || '–'}</td>
                    <td className="px-2 py-1.5 text-gray-600">{line.pListID || '–'}</td>
                    <td className="px-2 py-1.5 text-gray-400">{formatDateTime(line.created)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
